import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMapsPlaces } from '../../../../lib/googleMapsLoader';

/**
 * PropertyMap
 *
 * Lightweight read-only property view. Reuses the existing Maps JS loader
 * (googleMapsLoader.js) and API key already powering Calculator +
 * PlacesAutocompleteInput. No new GCP API surface.
 *
 * Modes (parent-controlled via the `mode` prop):
 *   - 'satellite'   — google.maps.Map with mapTypeId='satellite' + marker
 *   - 'street'      — google.maps.StreetViewPanorama (same SDK call used
 *                     by PropertyInfoPanel + QuoteWorkspace)
 *
 * Street View availability is checked via google.maps.StreetViewService
 * BEFORE the panorama mounts. When no imagery exists within ~50m, the
 * component surfaces an `onStreetViewAvailability(false)` callback so the
 * parent can disable / explain the toggle. Satellite remains available
 * in all cases.
 *
 * Read-only contract:
 *   - mapTypeId locked to 'satellite'
 *   - All editing UI disabled
 *   - DrawingManager NEVER imported — drawing physically impossible
 *   - Operator pan + zoom only
 */
const PropertyMap = ({
  lat,
  lng,
  title,
  mode = 'satellite',
  onStreetViewAvailability,
  className = '',
  testId = 'property-map',
}) => {
  const containerRef = useRef(null);
  const instanceRef = useRef(null);   // Map or StreetViewPanorama
  const markerRef = useRef(null);
  const [error, setError] = useState(null);
  const [streetViewMissing, setStreetViewMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (typeof lat !== 'number' || typeof lng !== 'number') return undefined;
    setError(null);
    setStreetViewMissing(false);

    loadGoogleMapsPlaces()
      .then((google) => {
        if (cancelled || !containerRef.current) return;

        if (mode === 'street') {
          // ── Street View branch ──────────────────────────────────────────
          // Check imagery availability before mounting the panorama. If we
          // mount blindly the SDK paints a black/grey empty viewport with
          // no operator-facing affordance to recover.
          const svc = new google.maps.StreetViewService();
          svc.getPanorama(
            { location: { lat, lng }, radius: 50 },
            (data, status) => {
              if (cancelled || !containerRef.current) return;
              if (status !== google.maps.StreetViewStatus.OK || !data?.location) {
                setStreetViewMissing(true);
                onStreetViewAvailability?.(false);
                return;
              }
              try {
                const panorama = new google.maps.StreetViewPanorama(
                  containerRef.current,
                  {
                    // Use the panorama's authoritative location, not the
                    // input lat/lng — Google may have nearest-road snapped.
                    position: data.location.latLng,
                    pov: { heading: 0, pitch: 0 },
                    zoom: 1,
                    disableDefaultUI: true,
                    zoomControl: true,
                    fullscreenControl: true,
                    addressControl: false,
                    showRoadLabels: false,
                    motionTracking: false,
                    motionTrackingControl: false,
                    clickToGo: false,
                  },
                );
                instanceRef.current = panorama;
                onStreetViewAvailability?.(true);
              } catch (err) {
                console.error('[PropertyMap] StreetView mount failed:', err);
                setError('Could not load Street View');
                onStreetViewAvailability?.(false);
              }
            },
          );
          return;
        }

        // ── Satellite branch ────────────────────────────────────────────
        const map = new google.maps.Map(containerRef.current, {
          center: { lat, lng },
          zoom:   20,
          mapTypeId: 'satellite',
          disableDefaultUI:    true,
          mapTypeControl:      false,
          streetViewControl:   false,
          rotateControl:       false,
          fullscreenControl:   true,
          zoomControl:         true,
          scaleControl:        false,
          keyboardShortcuts:   false,
          clickableIcons:      false,
          gestureHandling:     'cooperative',
        });
        instanceRef.current = map;
        markerRef.current = new google.maps.Marker({
          position: { lat, lng },
          map,
          title: title || 'Property',
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[PropertyMap] Failed to load Google Maps:', err);
        setError(err?.message || 'Could not load map');
      });

    return () => {
      cancelled = true;
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      instanceRef.current = null;
    };
  }, [lat, lng, title, mode, onStreetViewAvailability]);

  if (typeof lat !== 'number' || typeof lng !== 'number') return null;

  return (
    <div
      className={`relative rounded-md overflow-hidden border border-gray-200 bg-gray-100 ${className}`}
      style={{ aspectRatio: '16 / 9' }}
      data-testid={testId}
      data-mode={mode}
    >
      <div ref={containerRef} className="absolute inset-0" />

      {streetViewMissing && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 text-center px-4 z-10"
          data-testid={`${testId}-street-unavailable`}
        >
          <p className="text-sm font-medium text-gray-700">Street View not available here</p>
          <p className="text-xs text-gray-500 mt-1 max-w-[28rem]">
            Google doesn&apos;t have street-level imagery within ~50 m of this
            property. Switch back to Satellite to view the property from above.
          </p>
        </div>
      )}

      {error && !streetViewMissing && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-gray-50 text-xs text-gray-500 px-3 text-center z-10"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default PropertyMap;
